import * as React from "react";
import { Link } from "react-router";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { AlertOctagon, AlertTriangle, ChevronRight, Info } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * WarningCard — conditional banner über dem dashboard-hero.
 *
 * Severity-mapping (color-blind safe per icon + border-l-4):
 *   info     → cool-teal-soft bg, Info-icon, role="status"
 *   warning  → heat-2-soft bg (amber), AlertTriangle, role="status"
 *   critical → heat-4-soft bg (red), AlertOctagon, role="alert"
 *
 * Visual-distinktion zu hero-card: shadow-sm (subtiler), rounded-lg (12px
 * statt 16px). Slide-in-top beim mount (≤200ms, reduced-motion-respect in
 * main.css). Action ist optional sekundärer link (kein primary-button-look).
 */

const warningCardVariants = cva(
  [
    "relative flex gap-3 rounded-lg border border-[color:var(--color-border)]",
    "border-l-4 p-4 shadow-[var(--shadow-sm)] md:gap-4 md:p-5",
    "anim-slide-in-top",
  ].join(" "),
  {
    variants: {
      severity: {
        info: "bg-[color:var(--color-cool-teal-50)] border-l-[color:var(--color-info)]",
        warning: "bg-[color:var(--color-heat-2-soft)] border-l-[color:var(--color-warning)]",
        critical: "bg-[color:var(--color-heat-4-soft)] border-l-[color:var(--color-danger)]",
      },
    },
    defaultVariants: { severity: "info" },
  },
);

type Severity = NonNullable<VariantProps<typeof warningCardVariants>["severity"]>;

const ICON_COLOR_BY_SEVERITY: Record<Severity, string> = {
  info: "text-[color:var(--color-info)]",
  warning: "text-[color:var(--color-warning)]",
  critical: "text-[color:var(--color-danger)]",
};

const DEFAULT_ICON_BY_SEVERITY: Record<Severity, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertOctagon,
};

export interface WarningCardProps {
  severity: Severity;
  title: string;
  description?: string;
  action?: { label: string; href: string };
  icon?: LucideIcon;
  children?: React.ReactNode;
  className?: string;
}

export function WarningCard({
  severity,
  title,
  description,
  action,
  icon,
  children,
  className,
}: WarningCardProps): React.ReactElement {
  const Icon = icon ?? DEFAULT_ICON_BY_SEVERITY[severity];
  const role = severity === "critical" ? "alert" : "status";
  return (
    <section role={role} className={cn(warningCardVariants({ severity }), className)}>
      <Icon
        aria-hidden="true"
        strokeWidth={2}
        className={cn("mt-0.5 size-5 shrink-0", ICON_COLOR_BY_SEVERITY[severity])}
      />
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold leading-tight text-[color:var(--color-text-primary)] md:text-base">
          {title}
        </h3>
        {description && (
          <p className="mt-1 text-sm leading-normal text-[color:var(--color-text-secondary)]">
            {description}
          </p>
        )}
        {children && <div className="mt-2">{children}</div>}
        {action && (
          <Link
            to={action.href}
            className={cn(
              "mt-3 inline-flex min-h-[var(--size-touch-min)] items-center gap-1 rounded-md",
              "-mx-2 px-2 py-2 text-sm font-medium",
              "text-[color:var(--color-text-primary)] underline-offset-4 hover:underline",
              "transition-colors duration-150 ease-out",
            )}
          >
            {action.label}
            <ChevronRight aria-hidden="true" strokeWidth={2.25} className="size-4" />
          </Link>
        )}
      </div>
    </section>
  );
}

WarningCard.displayName = "WarningCard";
